<template>
  <div class="speaker-customization-panel">
    <div class="panel-header">
      <div class="panel-title-area">
        <h3 class="panel-title">Speaker Customization</h3>
        <span v-if="totalCustomizations > 0" class="customization-count">({{ totalCustomizations }})</span>
      </div>
      <button class="toggle-btn" @click="togglePanel">
        {{ isPanelExpanded ? '▼' : '▶' }}
      </button>
    </div>

    <div v-show="isPanelExpanded" class="panel-content">
      <!-- Voice Provider Selection -->
      <div class="settings-section">
        <h4 class="settings-title">Voice Settings</h4>

        <div class="provider-selection">
          <div class="customization-label">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </svg>
            <span>Voice Provider</span>
          </div>

          <div class="provider-buttons">
            <button
              v-for="provider in availableProviders"
              :key="provider.id"
              class="provider-btn"
              :class="{ 'active': selectedProvider === provider.id }"
              @click="selectProvider(provider.id)"
            >
              <component :is="provider.icon" class="provider-icon" />
              <span>{{ provider.name }}</span>
              <span class="provider-quality">{{ provider.quality }}</span>
            </button>
          </div>
        </div>

        <!-- ElevenLabs specific settings -->
        <div v-if="selectedProvider === 'elevenlabs'" class="provider-settings">
          <div class="elevenlabs-settings">
            <h5 class="subsection-title">ElevenLabs Settings</h5>

            <div class="setting-group">
              <div class="customization-label">
                <span>Voice Stability</span>
                <div class="help-tooltip" title="Higher values make the voice more consistent but less expressive">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9,9h0a3,3,0,0,1,5.12-2.12A3,3,0,0,1,15.92,9.5"></path>
                    <polyline points="12,17 12,13"></polyline>
                    <circle cx="12" cy="17" r=".5"></circle>
                  </svg>
                </div>
              </div>
              <div class="slider-container">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  v-model="elevenLabsSettings.stability"
                  class="setting-slider"
                  @change="handleElevenLabsSettingChange"
                />
                <span class="setting-value">{{ Math.round(elevenLabsSettings.stability * 100) }}%</span>
              </div>
            </div>

            <div class="setting-group">
              <div class="customization-label">
                <span>Similarity Boost</span>
                <div class="help-tooltip" title="How closely the AI should match the original voice">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9,9h0a3,3,0,0,1,5.12-2.12A3,3,0,0,1,15.92,9.5"></path>
                    <polyline points="12,17 12,13"></polyline>
                    <circle cx="12" cy="17" r=".5"></circle>
                  </svg>
                </div>
              </div>
              <div class="slider-container">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  v-model="elevenLabsSettings.similarity_boost"
                  class="setting-slider"
                  @change="handleElevenLabsSettingChange"
                />
                <span class="setting-value">{{ Math.round(elevenLabsSettings.similarity_boost * 100) }}%</span>
              </div>
            </div>

            <div class="setting-group">
              <div class="customization-label">
                <span>Style Exaggeration</span>
                <div class="help-tooltip" title="How much to exaggerate the speaking style">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9,9h0a3,3,0,0,1,5.12-2.12A3,3,0,0,1,15.92,9.5"></path>
                    <polyline points="12,17 12,13"></polyline>
                    <circle cx="12" cy="17" r=".5"></circle>
                  </svg>
                </div>
              </div>
              <div class="slider-container">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  v-model="elevenLabsSettings.style"
                  class="setting-slider"
                  @change="handleElevenLabsSettingChange"
                />
                <span class="setting-value">{{ Math.round(elevenLabsSettings.style * 100) }}%</span>
              </div>
            </div>

            <div class="setting-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  v-model="elevenLabsSettings.use_speaker_boost"
                  @change="handleElevenLabsSettingChange"
                />
                <span class="checkbox-custom"></span>
                <span>Enable Speaker Boost</span>
                <div class="help-tooltip" title="Enhances voice clarity and distinctness">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9,9h0a3,3,0,0,1,5.12-2.12A3,3,0,0,1,15.92,9.5"></path>
                    <polyline points="12,17 12,13"></polyline>
                    <circle cx="12" cy="17" r=".5"></circle>
                  </svg>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Background customization section -->
      <div class="settings-section">
        <h4 class="settings-title">Transcript Appearance</h4>

        <!-- Background opacity slider -->
        <div class="transcript-customization">
          <div class="customization-label">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="9" cy="9" r="2"></circle>
              <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
            </svg>
            <span>Background Opacity</span>
          </div>
          <div class="slider-container">
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              v-model="transcriptOpacity"
              class="opacity-slider"
              @change="handleTranscriptOpacityChange"
            />
            <span class="opacity-value">{{ Math.round(transcriptOpacity * 100) }}%</span>
          </div>
        </div>

        <!-- Background color picker -->
        <div class="transcript-customization">
          <div class="customization-label">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
              <circle cx="12" cy="12" r="2"></circle>
            </svg>
            <span>Background Color</span>
          </div>
          <div class="color-picker-container">
            <div class="current-color" :style="{ backgroundColor: transcriptBackgroundColor }"></div>
            <input
              type="color"
              v-model="transcriptBackgroundColor"
              class="color-picker"
              @change="handleTranscriptColorChange"
            />
            <button class="reset-button" @click="resetTranscriptBackground">Reset to Default</button>
          </div>
        </div>

        <h4 class="settings-title">Podcast Background</h4>
        <div class="background-upload">
          <label :for="`background-upload-${messageId}`" class="upload-bg-label">
            <div class="bg-preview-container">
              <img
                v-if="backgroundImage"
                :src="backgroundImage"
                class="bg-preview"
                alt="Background Preview"
              />
              <div v-else class="bg-placeholder">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                  <circle cx="9" cy="9" r="2"></circle>
                  <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                </svg>
                <span>Upload Background</span>
              </div>
            </div>
          </label>
          <input
            :id="`background-upload-${messageId}`"
            type="file"
            accept="image/*"
            class="file-input"
            @change="handleBackgroundUpload"
          />
          <button
            v-if="backgroundImage"
            class="reset-background-button"
            @click="resetBackgroundImage"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 6h18"></path>
              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
            </svg>
            Reset Background
          </button>
        </div>
      </div>

      <!-- Speaker customization -->
      <div class="settings-section">
        <h4 class="settings-title">Customize Speakers</h4>
        <div class="speakers-grid">
          <div
            v-for="(speaker, index) in internalSpeakers"
            :key="`customize-${index}`"
            class="speaker-customize-item"
          >
            <div class="speaker-image-upload">
              <label :for="`image-upload-${messageId}_${index}`" class="upload-label">
                <div class="preview-container">
                  <img
                    v-if="uploadedImages[`${messageId}_${index}`]"
                    :src="uploadedImages[`${messageId}_${index}`]"
                    class="image-preview"
                    alt="Preview"
                  />
                  <div v-else class="upload-placeholder">
                    <span>{{ speaker.name }}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M12 5v14"></path>
                      <path d="M5 12h14"></path>
                    </svg>
                  </div>
                </div>
              </label>
              <input
                :id="`image-upload-${messageId}_${index}`"
                type="file"
                accept="image/*"
                class="file-input"
                @change="(e) => handleImageUpload(e, index)"
              />
            </div>
            <button
              v-if="uploadedImages[`${messageId}_${index}`]"
              class="reset-image-button"
              @click="resetSpeakerImage(index)"
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 6h18"></path>
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
              </svg>
              Reset Image
            </button>

            <div class="speaker-fields">
              <input
                type="text"
                v-model="internalSpeakers[index].name"
                class="name-input"
                placeholder="Name"
                @change="handleNameChange(index)"
              />

              <input
                type="text"
                v-model="internalSpeakers[index].role"
                class="role-input"
                placeholder="Role"
                @change="handleRoleChange(index)"
              />

              <!-- Dynamic voice selection based on provider -->
              <div class="voice-select-container" :class="{ 'voice-selected': selectedVoices[index] }">
                <div class="voice-select-label">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                    <line x1="12" y1="19" x2="12" y2="23"></line>
                    <line x1="8" y1="23" x2="16" y2="23"></line>
                  </svg>
                  <span>{{ getProviderName(selectedProvider) }} Voice</span>
                </div>

                <select
                  v-model="selectedVoices[index]"
                  class="voice-select"
                  @change="handleVoiceChange(index)"
                >
                  <option value="">Default Voice</option>
                  <template v-if="selectedProvider === 'elevenlabs'">
                    <optgroup label="Popular Voices">
                      <option v-for="voice in elevenLabsPopularVoices" :key="voice.id" :value="voice.id">
                        {{ voice.name }} - {{ voice.description }}
                      </option>
                    </optgroup>
                    <optgroup label="All Voices" v-if="elevenLabsAllVoices.length > 0">
                      <option v-for="voice in elevenLabsAllVoices" :key="voice.id" :value="voice.id">
                        {{ voice.name }}{{ voice.gender ? ` (${voice.gender})` : '' }}
                      </option>
                    </optgroup>
                  </template>
                  <template v-else-if="selectedProvider === 'local'">
                    <optgroup label="Female">
                      <option v-for="voice in femaleVoices" :key="`female-${voice}`" :value="voice">
                        {{ getReadableVoiceName(voice) }}
                      </option>
                    </optgroup>
                    <optgroup label="Male">
                      <option v-for="voice in maleVoices" :key="`male-${voice}`" :value="voice">
                        {{ getReadableVoiceName(voice) }}
                      </option>
                    </optgroup>
                  </template>
                  <template v-else-if="selectedProvider === 'google'">
                    <optgroup label="Standard Voices">
                      <option value="en-US-Standard-A">en-US-Standard-A (Female)</option>
                      <option value="en-US-Standard-B">en-US-Standard-B (Male)</option>
                      <option value="en-US-Standard-C">en-US-Standard-C (Female)</option>
                      <option value="en-US-Standard-D">en-US-Standard-D (Male)</option>
                    </optgroup>
                    <optgroup label="Neural Voices">
                      <option value="en-US-Neural2-A">en-US-Neural2-A (Female)</option>
                      <option value="en-US-Neural2-C">en-US-Neural2-C (Female)</option>
                      <option value="en-US-Neural2-D">en-US-Neural2-D (Male)</option>
                      <option value="en-US-Neural2-F">en-US-Neural2-F (Female)</option>
                    </optgroup>
                  </template>
                </select>

                <!-- ElevenLabs voice preview button -->
                <button
                  v-if="selectedProvider === 'elevenlabs' && selectedVoices[index]"
                  class="voice-preview-btn"
                  @click="previewVoice(selectedVoices[index])"
                  :disabled="isPreviewLoading"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polygon points="5,3 19,12 5,21"></polygon>
                  </svg>
                  {{ isPreviewLoading ? 'Loading...' : 'Preview' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import API from '@/api';
import { mapActions } from 'vuex';

export default {
  name: 'SpeakerCustomizationPanel',
  props: {
    isPro: {
      type: Boolean,
      default: false
    },
    messageId: {
      required: true
    },
    speakers: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      isPanelExpanded: false,
      transcriptOpacity: 0.6,
      transcriptBackgroundColor: '#000000',
      internalSpeakers: JSON.parse(JSON.stringify(this.speakers)),
      uploadedImages: {},
      backgroundImage: null,
      selectedVoices: {},
      selectedProvider: 'local', // Default to local
      isPreviewLoading: false,

      // ElevenLabs settings
      elevenLabsSettings: {
        stability: 0.5,
        similarity_boost: 0.75,
        style: 0.5,
        use_speaker_boost: true
      },

      // Available providers
      availableProviders: [
        {
          id: 'local',
          name: 'Local',
          quality: 'Fast',
          icon: 'LocalIcon'
        },
        {
          id: 'elevenlabs',
          name: 'ElevenLabs',
          quality: 'Premium',
          icon: 'ElevenLabsIcon'
        },
      ],

      // ElevenLabs popular voices
      elevenLabsPopularVoices: [
        { id: '8wTyTeMNPIEBRWcPf00u', name: 'Felix', description: 'Engaging thoughful voice for explaing concepts' },
        { id: 'dAZqM8Pl37bzdPFxvgXm', name: 'Christopher Collins', description: 'Engaging Male Conversational' },
        { id: 'Dslrhjl3ZpzrctukrQSN', name: 'Hey, Its Brad', description: 'Versaltile Male Voice' },
        { id: 'DTKMou8ccj1ZaWGBiotd', name: 'Jamahal', description: 'Stylish, Young, Urban Male' },
        { id: 'pgXLagTlUQcy1LT68JCe', name: 'Larry', description: 'A clear friendly male voice'},
        { id: 'vBKc2FfBKJfcZNyEt1n6', name: 'Finn', description: 'Young, Male, Energetic'},
        { id: 'aMSt68OGf4xUZAnLpTU8', name: 'Juniper', description: 'Female Podcaster, conversational' },
        { id: '56AoDkrOh6qfVPDXZ7Pt', name: 'Cassidy', description: 'A confident female podcaster with plethora of experience in the music industry.'},
        { id: 'rSZFtT0J8GtnLqoDoFAp', name: 'Nova', description: 'Raspy Comforting Female voice with gentle huskiness'},
        { id: 'M563YhMmA0S8vEYwkgYa', name: 'Sammy', description: 'A fun, cute and androgynpus voice' },
        { id: '5l5f8iK3YPeGga21rQIX', name: 'Adeline', description: 'Conversational Female voice perfect for narration' },
        { id: 'KoVIHoyLDrQyd4pGalbs', name: 'Autumn Veil', description: 'Vulnerability Meets confidence' },
        { id: 'P7x743VjyZEOihNNygQ9', name: 'Dakota', description: 'Middle-aged african american southern' },
        { id: 'XRdIKD2HKD2sMJjeC483', name: 'Sachin', description: 'Deep and Thoughtful Male Hindi Voice' },
        { id: 'MF4J4IDTRo0AxOO4dpFR', name: 'Devi', description: 'Young Indian Girl Hindi Voice' },
        { id: 'nsuWityFzyjklsOeHYMt', name: 'Netra', description: 'Warm and Conversational Hindi Voice'},
        { id: 'FZkK3TvQ0pjyDmT8fzIW', name: 'Bunty', description: 'Young Male Hindi Voice' },
        { id: 'gedzfqL7OGdPbwm0ynTP', name: 'Nadia', description: 'Russian' },
        { id: 'aEO01A4wXwd1O8GPgGlF', name: 'Arabella', description: 'Australian' }

      ],

      // All ElevenLabs voices (loaded from API)
      elevenLabsAllVoices: [
        // Hindi
      ],

      // Local voices (existing)
      femaleVoices: [
        'af_alloy', 'af_aoede', 'af_bella', 'af_heart', 'af_jadzia', 'af_jessica',
        'af_kore', 'af_nicole', 'af_nova', 'af_river', 'af_sarah', 'af_sky',
        'af_v0', 'af_v0bella', 'af_v0irulan', 'af_v0nicole', 'af_v0sarah', 'af_v0sky',
        'bf_alice', 'bf_emma', 'bf_lily', 'bf_v0emma', 'bf_v0isabella',
        'ef_dora', 'ff_siwis', 'hf_alpha', 'hf_beta', 'if_sara', 'jf_alpha',
        'jf_gongitsune', 'jf_nezumi', 'jf_tebukuro', 'pf_dora', 'zf_xiaobei',
        'zf_xiaoni', 'zf_xiaoxiao', 'zf_xiaoyi'
      ],
      maleVoices: [
        'am_adam', 'am_echo', 'am_eric', 'am_fenrir', 'am_liam', 'am_michael',
        'am_onyx', 'am_puck', 'am_santa', 'am_v0adam', 'am_v0gurney', 'am_v0michael',
        'bm_daniel', 'bm_fable', 'bm_george', 'bm_lewis', 'bm_v0george', 'bm_v0lewis',
        'em_alex', 'em_santa', 'hm_omega', 'hm_psi', 'im_nicola', 'jm_kumo',
        'pm_alex', 'pm_santa', 'zm_yunjian', 'zm_yunxi', 'zm_yunxia', 'zm_yunyang'
      ]
    }
  },
  components: {
    LocalIcon: {
      template: `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="2" y="4" width="20" height="16" rx="2"/>
          <path d="M7 15h.01M11 15h.01M15 15h.01M7 11h8"/>
        </svg>
      `
    },
    ElevenLabsIcon: {
      template: `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
          <line x1="12" y1="19" x2="12" y2="23"/>
          <line x1="8" y1="23" x2="16" y2="23"/>
        </svg>
      `
    },
    GoogleIcon: {
      template: `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <polygon points="10,8 14,12 10,16 10,8"/>
        </svg>
      `
    }
  },
  computed: {
    totalCustomizations() {
      let count = 0;

      // Count transcript customizations
      if (this.transcriptOpacity !== 0.6 || this.transcriptBackgroundColor !== '#000000') {
        count++;
      }

      // Count background image
      if (this.backgroundImage) {
        count++;
      }

      // Count speaker customizations
      this.internalSpeakers.forEach((speaker, index) => {
        const key = `${this.messageId}_${index}`;
        if (this.uploadedImages[key]) count++;
        if (this.selectedVoices[index]) count++;
      });

      // Count provider selection
      if (this.selectedProvider !== 'local') {
        count++;
      }

      return count;
    }
  },
  watch: {
    speakers: {
      handler(newSpeakers) {
        this.internalSpeakers = JSON.parse(JSON.stringify(newSpeakers));
      },
      deep: true,
      immediate: true
    }
  },
  async mounted() {
    try {
      this.initializeDefaultVoices();
      this.applyExistingCustomizations();
      await this.loadCustomizationData();

      // Load ElevenLabs voices if provider is selected
      if (this.selectedProvider === 'elevenlabs') {
        await this.loadElevenLabsVoices();
      }

    } catch (error) {
      console.error('Error loading customization data:', error);
    }
  },
  methods: {
    ...mapActions('websocket', ['showTempMessageFn']),

    // Provider selection methods
    async selectProvider(providerId) {
      // only for pro users
      if (!this.requiresPro(`Switching to ${this.getProviderName(providerId)} provider`)) {
        return;
      }
      this.selectedProvider = providerId;

      // Clear existing voice selections when switching providers
      this.selectedVoices = {};

      // Load provider-specific voices
      if (providerId === 'elevenlabs') {
        await this.loadElevenLabsVoices();
      }

      // Emit provider change
      this.$emit('provider-changed', {
        provider: providerId,
        settings: providerId === 'elevenlabs' ? this.elevenLabsSettings : null
      });

      // Save the provider selection
      await this.saveSpeakerCustomizations();
    },

    getProviderName(providerId) {
      const provider = this.availableProviders.find(p => p.id === providerId);
      return provider ? provider.name : 'Unknown';
    },

    // ElevenLabs specific methods
    async loadElevenLabsVoices() {
      try {
        // You can implement API call to get user's ElevenLabs voices
        // For now, we'll use the popular voices as all voices
        this.elevenLabsAllVoices = [...this.elevenLabsPopularVoices];

        // TODO: Implement actual API call to ElevenLabs
        // const response = await API.get('/tts/elevenlabs/voices');
        // this.elevenLabsAllVoices = response.data.voices;
      } catch (error) {
        console.error('Error loading ElevenLabs voices:', error);
        this.showTempMessageFn({
          message: 'Failed to load ElevenLabs voices. Using default voices.',
          type: 'warning',
          timeout: 3000
        });
      }
    },

    async previewVoice(voiceId) {
      if (!voiceId || this.isPreviewLoading) return;

      this.isPreviewLoading = true;

      try {
        const token = localStorage.getItem("token");
        const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");
        const url = token ? '/tts' : `/tts/anonymous/${unstruct_anonymous_key}`;

        const response = await API.post(url, {
          text: "Hello, this is a preview of this voice.",
          provider: 'elevenlabs',
          voice: voiceId,
          speed: 1.0
        }, {
          responseType: 'arraybuffer',
          headers: {
            'Accept': 'audio/mpeg',
            'Content-Type': 'application/json'
          }
        });

        // Create and play audio
        const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioUrl);

        audio.play().catch(error => {
          console.error('Error playing preview:', error);
          this.showTempMessageFn({
            message: 'Error playing voice preview',
            type: 'error',
            timeout: 3000
          });
        });

        // Clean up URL after playing
        audio.addEventListener('ended', () => {
          URL.revokeObjectURL(audioUrl);
        });

      } catch (error) {
        console.error('Error previewing voice:', error);
        this.showTempMessageFn({
          message: 'Error generating voice preview',
          type: 'error',
          timeout: 3000
        });
      } finally {
        this.isPreviewLoading = false;
      }
    },

    handleElevenLabsSettingChange() {
      // Emit settings change
      this.$emit('elevenlabs-settings-changed', this.elevenLabsSettings);

      // Save settings
      this.saveSpeakerCustomizations();
    },

    // Existing methods (keeping all the original functionality)
    async resetBackgroundImage() {
      if (!this.requiresPro('Resetting background image')) {
        return;
      }

      try {
        await API.delete(`/user_customization/messages/${this.messageId}/background`);
        this.backgroundImage = null;
        this.$emit('background-reset', { messageId: this.messageId });
        this.updateRelatedComponents();
        await this.saveSpeakerCustomizations();
        this.showTempMessageFn('Background image has been reset', 'success');
      } catch (error) {
        console.error('Error resetting background image:', error);
        this.showTempMessageFn('Failed to reset background image', 'error');
      }
    },

    async resetSpeakerImage(index) {
      if (!this.requiresPro('Resetting speaker images')) {
        return;
      }

      try {
        const uniqueKey = `${this.messageId}_${index}`;
        await API.delete(`/user_customization/messages/${this.messageId}/speakers/${index}/image`);
        this.$delete(this.uploadedImages, uniqueKey);
        this.$emit('image-reset', {
          messageId: this.messageId,
          index,
          uniqueKey,
          speakerName: this.internalSpeakers[index].name,
        });
        this.updateRelatedComponents();
        this.showTempMessageFn('Speaker image has been reset', 'success');
      } catch (error) {
        console.error('Error resetting speaker image:', error);
        this.showTempMessageFn('Failed to reset speaker image', 'error');
      }
    },

    togglePanel() {
      this.isPanelExpanded = !this.isPanelExpanded;
    },

    requiresPro(action, callback) {
      if (this.isPro) {
        // User is Pro - allow the action
        if (callback) callback();
        return true;
      } else {
        // User is not Pro - show upgrade prompt
        this.showUpgradePrompt(action);
        return false;
      }
    },

    showUpgradePrompt(action) {
      // You could emit an event to show a modal, or use a more sophisticated notification
      this.$emit('show-upgrade-modal', {
        feature: action,
        message: `${action} is a Pro feature. Upgrade to unlock advanced customization options.`
      });

      // Also show a temporary message
      this.showTempMessageFn({
        message: `${action} requires Pro subscription`,
        type: 'upgrade',
        timeout: 3000,
        action: {
          text: 'Upgrade',
          handler: () => {
            // Redirect to upgrade page or show upgrade modal
            this.$emit('trigger-upgrade');
          }
        }
      });
    },

    handleTranscriptOpacityChange() {
      this.requiresPro('Adjusting transcript opacity', () => {
        this.updateTranscriptBackground();
      });
    },

    handleTranscriptColorChange() {
      this.requiresPro('Changing transcript color', () => {
        this.updateTranscriptBackground();
      });
    },

    resetTranscriptBackground() {
      this.requiresPro('Resetting transcript background', () => {
        this.transcriptOpacity = 0.6;
        this.transcriptBackgroundColor = '#000000';
        this.updateTranscriptBackground();
      });
    },

    updateTranscriptBackground(fromServer = false) {
      if (this.transcriptBackgroundColor.startsWith('rgba(') && fromServer) {
        this.$emit('transcript-background-changed', this.transcriptBackgroundColor);
        this.saveTranscriptCustomization(this.transcriptBackgroundColor);
        return;
      }

      const hex = this.transcriptBackgroundColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const backgroundColor = `rgba(${r}, ${g}, ${b}, ${this.transcriptOpacity})`;

      this.$emit('transcript-background-changed', backgroundColor);
      localStorage.setItem(`transcript_background_${this.messageId}`, backgroundColor);
      this.saveTranscriptCustomization(backgroundColor);
    },

    async saveTranscriptCustomization(backgroundColor) {
      if (!localStorage.getItem('token')) return;
      try {
        await API.post(
          `/user_customization/messages/${this.messageId}/transcript-customization`,
          {
            background_color: backgroundColor,
            opacity: this.transcriptOpacity,
            color: this.transcriptBackgroundColor
          }
        );
      } catch (error) {
        console.error('Error saving transcript customization:', error);
      }
    },

    initializeDefaultVoices() {
      this.internalSpeakers.forEach((speaker, index) => {
        if (this.selectedVoices[index]) return;

        const name = speaker.name.toLowerCase();
        const role = (speaker.role || '').toLowerCase();
        let isLikelyMale = false;

        if (role.includes('host') || role.includes('moderator')) {
          isLikelyMale = Math.random() > 0.5;
        } else if (role.includes('mr') || role.includes('male') || role.includes('man') ||
                  role.includes('father') || role.includes('brother') || role.includes('uncle')) {
          isLikelyMale = true;
        }

        // Set default voice based on provider
        if (this.selectedProvider === 'elevenlabs') {
          // Default to popular ElevenLabs voices
          const defaultVoice = isLikelyMale ?
            this.elevenLabsPopularVoices.find(v => v.name === 'Antoni') || this.elevenLabsPopularVoices[3] :
            this.elevenLabsPopularVoices.find(v => v.name === 'Rachel') || this.elevenLabsPopularVoices[0];
          this.$set(this.selectedVoices, index, defaultVoice.id);
        } else if (this.selectedProvider === 'local') {
          // Use existing local voice logic
          if (isLikelyMale) {
            this.$set(this.selectedVoices, index, this.maleVoices[0]);
          } else {
            this.$set(this.selectedVoices, index, this.femaleVoices[0]);
          }
        }
      });

      this.$forceUpdate();
    },

    async loadCustomizationData() {
      try {
        const token = localStorage.getItem('token');
        const unstruct_anonymous_key = localStorage.getItem("unstruct_anonymous_key");

        const url = token ? `/user_customization/messages/${this.messageId}/customize-data` : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/customize-data`;
        const response = await API.get(url);
        const data = response.data;
        // Load transcript customization
        if (data.transcriptCustomization) {
          const bgColor = data.transcriptCustomization.background_color;
          const color = data.transcriptCustomization.color;
          const opacity = data.transcriptCustomization.opacity;
          if (bgColor && opacity) {
            this.transcriptBackgroundColor = color;
            this.transcriptOpacity = data.transcriptCustomization.opacity;
            localStorage.setItem(`transcript_background_${this.messageId}`, color);
            this.updateTranscriptBackground(true);
          }
        }

        // Load speaker images
        if (data.speakerImages) {
          Object.keys(data.speakerImages).forEach(index => {
            const img = data.speakerImages[index];
            const uniqueKey = `${this.messageId}_${index}`;
            this.$set(this.uploadedImages, uniqueKey, img.url);
          });
        }

        // Load background image
        if (data.background) {
          this.backgroundImage = data.background.url;
          this.$emit('background-uploaded', {
            imageUrl: data.background.url,
            id: data.background.id
          });
        }

        // Get speaker customizations
        const speakers_url = token ? `/user_customization/messages/${this.messageId}/speaker-customizations` : `/user_customization/anonymous/${unstruct_anonymous_key}/messages/${this.messageId}/speaker-customizations`;
        const speakerCustomResponse = await API.get(speakers_url);
        const customData = speakerCustomResponse.data;


        // Load provider selection
        if (customData && customData.voice_provider) {
          this.selectedProvider = customData.voice_provider;
          this.selectProvider(this.selectedProvider);
        }

        // Load ElevenLabs settings
        if (customData && customData.elevenlabs_settings) {
          this.elevenLabsSettings = { ...this.elevenLabsSettings, ...customData.elevenlabs_settings };
        }


        if (customData && customData.speakers && customData.speakers.length) {
          customData.speakers.forEach(customSpeaker => {
            if (customSpeaker.speaker_index !== undefined &&
                customSpeaker.speaker_index < this.internalSpeakers.length) {

              const index = customSpeaker.speaker_index;

              if (customSpeaker.custom_name) {
                this.internalSpeakers[index].name = customSpeaker.custom_name;
              }

              if (customSpeaker.custom_role) {
                this.internalSpeakers[index].role = customSpeaker.custom_role;
              }

              if (customSpeaker.voice_id) {
                this.$set(this.selectedVoices, index, customSpeaker.voice_id);
              }
            } else {
              const speakerIndex = this.internalSpeakers.findIndex(s =>
                s.name === customSpeaker.original_name
              );

              if (speakerIndex >= 0) {
                if (customSpeaker.custom_name) {
                  this.internalSpeakers[speakerIndex].name = customSpeaker.custom_name;
                }

                if (customSpeaker.custom_role) {
                  this.internalSpeakers[speakerIndex].role = customSpeaker.custom_role;
                }

                if (customSpeaker.voice_id) {
                  this.$set(this.selectedVoices, speakerIndex, customSpeaker.voice_id);
                }
              }
            }
          });

          this.$forceUpdate();
          this.$emit('customizations-loaded', customData);
        }

        return customData;
      } catch (error) {
        console.error('Error loading customization data:', error);
        return null;
      }
    },

    updateRelatedComponents() {
      this.$emit('ui-update-needed');
      this.$forceUpdate();
    },

    applyExistingCustomizations() {
      if (this.speakers && this.speakers.length) {
        this.internalSpeakers = JSON.parse(JSON.stringify(this.speakers));
      }
    },

    async saveSpeakerCustomizations() {
      try {
        const speakerData = this.internalSpeakers.map((speaker, index) => ({
          original_name: this.speakers[index].name,
          custom_name: speaker.name,
          original_role: this.speakers[index].role,
          custom_role: speaker.role,
          voice_id: this.selectedVoices[index] || null,
          speaker_index: index
        }));

        const customizations = {
          speakers: speakerData,
          voice_provider: this.selectedProvider,
          elevenlabs_settings: this.selectedProvider === 'elevenlabs' ? this.elevenLabsSettings : null,
          background_settings: {
            use_custom_background: !!this.backgroundImage
          }
        };

        await API.post(
          `/user_customization/messages/${this.messageId}/speaker-customizations`,
          customizations
        );

      } catch (error) {
        console.error('Error saving speaker customizations:', error);
      }
    },

    async handleBackgroundUpload(event) {
      if (!this.requiresPro('Uploading custom backgrounds')) {
        event.target.value = '';
        return;
      }

      const file = event.target.files[0];
      if (!file) return;

      if (file.type.startsWith('image/') || file.name.endsWith('.webp')) {
        try {
          const formData = new FormData();
          formData.append('file', file);

          const response = await API.post(`/user_customization/messages/${this.messageId}/background`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          this.backgroundImage = response.data.url;

          this.$emit('background-uploaded', {
            imageUrl: response.data.url,
            id: response.data.id
          });
        } catch (error) {
          console.error('Error uploading background image:', error);
        }
      }
    },

    getReadableVoiceName(voiceId) {
      if (!voiceId) return 'Default Voice';

      const parts = voiceId.split('_');
      if (parts.length < 2) return voiceId;

      let gender = '';
      let region = '';
      let name = parts[1];

      switch(parts[0]) {
        case 'af': gender = 'Female'; region = 'American'; break;
        case 'am': gender = 'Male'; region = 'American'; break;
        case 'bf': gender = 'Female'; region = 'British'; break;
        case 'bm': gender = 'Male'; region = 'British'; break;
        case 'ef': gender = 'Female'; region = 'Spanish'; break;
        case 'em': gender = 'Male'; region = 'Spanish'; break;
        case 'hf': gender = 'Female'; region = 'Hindi'; break;
        case 'hm': gender = 'Male'; region = 'Hindi'; break;
        case 'if': gender = 'Female'; region = 'Italian'; break;
        case 'im': gender = 'Male'; region = 'Italian'; break;
        case 'jf': gender = 'Female'; region = 'Japanese'; break;
        case 'jm': gender = 'Male'; region = 'Japanese'; break;
        case 'pf': gender = 'Female'; region = 'Portuguese'; break;
        case 'pm': gender = 'Male'; region = 'Portuguese'; break;
        case 'zf': gender = 'Female'; region = 'Chinese'; break;
        case 'zm': gender = 'Male'; region = 'Chinese'; break;
        default: return voiceId;
      }

      name = name.charAt(0).toUpperCase() + name.slice(1);
      return `${name} (${region})`;
    },

    async handleImageUpload(event, index) {
      if (!this.requiresPro('Uploading speaker images')) {
        event.target.value = '';
        return;
      }

      const file = event.target.files[0];
      if (!file) return;

      if (file.type.startsWith('image/') || file.name.endsWith('.webp')) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('original_name', this.internalSpeakers[index].name);

          const response = await API.post(
            `/user_customization/messages/${this.messageId}/speakers/${index}/image`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          const uniqueKey = `${this.messageId}_${index}`;
          this.$set(this.uploadedImages, uniqueKey, response.data.url);

          this.$emit('image-uploaded', {
            messageId: this.messageId,
            index,
            speakerName: this.internalSpeakers[index].name,
            imageUrl: response.data.url,
            id: response.data.id
          });
        } catch (error) {
          console.error('Error uploading speaker image:', error);
        }
      }
    },

    async handleNameChange(index) {
      if (!this.requiresPro('Customizing speaker names')) {
        this.internalSpeakers[index].name = this.speakers[index].name;
        this.$forceUpdate();
        return;
      }

      const oldName = this.speakers[index].name;
      const newName = this.internalSpeakers[index].name;

      if (oldName === newName) return;

      this.$emit('name-changed', {
        index,
        newName,
        oldName
      });

      await this.saveSpeakerCustomizations();

      setTimeout(() => {
        if (this.activeSpeaker === oldName) {
          this.$emit('speaker-selected', newName);
        }
      }, 100);
    },

    async handleRoleChange(index) {
      if (!this.requiresPro('Customizing speaker roles')) {
        this.internalSpeakers[index].role = this.speakers[index].role;
        this.$forceUpdate();
        return;
      }

      this.$emit('role-changed', {
        index,
        speakerName: this.internalSpeakers[index].name,
        newRole: this.internalSpeakers[index].role,
        oldRole: this.speakers[index].role
      });

      await this.saveSpeakerCustomizations();
    },

    async handleVoiceChange(index) {
      if (!this.requiresPro('Selecting custom voices')) {
        this.$set(this.selectedVoices, index, '');
        this.$forceUpdate();
        return;
      }

      this.$emit('voice-changed', {
        index,
        speakerName: this.internalSpeakers[index].name,
        voiceId: this.selectedVoices[index] || '',
        provider: this.selectedProvider,
        settings: this.selectedProvider === 'elevenlabs' ? this.elevenLabsSettings : null
      });

      await this.saveSpeakerCustomizations();
    }
  }
}
</script>


<style scoped>
.speaker-customization-panel {
  background-color: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
  margin-bottom: 1rem;
}

.theme--dark .speaker-customization-panel {
  background-color: #1a202c;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.panel-title-area {
  display: flex;
  align-items: center;
}

.panel-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.theme--dark .panel-title {
  color: #e2e8f0;
}

.customization-count {
  font-size: 0.75rem;
  color: #718096;
  margin-left: 0.25rem;
}

.toggle-btn {
  padding: 0.25rem 0.375rem;
  font-size: 0.875rem;
  color: #718096;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.theme--dark .toggle-btn {
  border-color: #2d3748;
  color: #a0aec0;
}

.toggle-btn:hover {
  background-color: #f7fafc;
}

.theme--dark .toggle-btn:hover {
  background-color: #2d3748;
}

.panel-content {
  margin-top: 0.75rem;
}

.settings-section {
  margin-bottom: 2rem;
}

.settings-section:last-child {
  margin-bottom: 0;
}

.settings-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #2d3748;
}

.theme--dark .settings-title {
  color: #e2e8f0;
}

/* Provider Selection Styles */
.provider-selection {
  margin-bottom: 1.5rem;
}

.provider-buttons {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.provider-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 0.75rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.theme--dark .provider-btn {
  border-color: #475569;
  background: #1e293b;
  color: #e2e8f0;
}

.provider-btn:hover {
  border-color: #cbd5e0;
  background: #f1f5f9;
  transform: translateY(-1px);
}

.theme--dark .provider-btn:hover {
  border-color: #64748b;
  background: #334155;
}

.provider-btn.active {
  border-color: #3b82f6;
  background: #dbeafe;
  color: #1d4ed8;
}

.theme--dark .provider-btn.active {
  border-color: #60a5fa;
  background: rgba(59, 130, 246, 0.2);
  color: #93c5fd;
}

.provider-icon {
  width: 20px;
  height: 20px;
}

.provider-quality {
  font-size: 0.75rem;
  opacity: 0.7;
  font-weight: 500;
}

/* ElevenLabs Settings Styles */
.provider-settings {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.theme--dark .provider-settings {
  background: #1e293b;
  border-color: #475569;
}

.elevenlabs-settings {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.subsection-title {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #374151;
}

.theme--dark .subsection-title {
  color: #d1d5db;
}

.setting-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.help-tooltip {
  cursor: help;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.help-tooltip:hover {
  opacity: 1;
}

.setting-slider {
  flex: 1;
  height: 6px;
  -webkit-appearance: none;
  background: #e5e7eb;
  border-radius: 3px;
  outline: none;
  transition: background 0.2s;
}

.theme--dark .setting-slider {
  background: #374151;
}

.setting-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  transition: background 0.2s;
}

.setting-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: none;
  transition: background 0.2s;
}

.setting-slider:hover::-webkit-slider-thumb,
.setting-slider:hover::-moz-range-thumb {
  background: #2563eb;
}

.setting-value {
  width: 45px;
  font-size: 0.875rem;
  text-align: right;
  color: #6b7280;
  font-weight: 500;
}

.theme--dark .setting-value {
  color: #9ca3af;
}

/* Checkbox Styles */
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
}

.theme--dark .checkbox-label {
  color: #d1d5db;
}

.checkbox-label input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.checkbox-custom {
  width: 16px;
  height: 16px;
  border: 2px solid #d1d5db;
  border-radius: 3px;
  position: relative;
  transition: all 0.2s;
}

.theme--dark .checkbox-custom {
  border-color: #6b7280;
}

.checkbox-label input[type="checkbox"]:checked + .checkbox-custom {
  background: #3b82f6;
  border-color: #3b82f6;
}

.checkbox-label input[type="checkbox"]:checked + .checkbox-custom::after {
  content: '✓';
  position: absolute;
  color: white;
  font-size: 12px;
  top: -2px;
  left: 2px;
}

/* Voice Preview Button */
.voice-preview-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  color: #374151;
}

.theme--dark .voice-preview-btn {
  background: #374151;
  border-color: #6b7280;
  color: #d1d5db;
}

.voice-preview-btn:hover:not(:disabled) {
  background: #e5e7eb;
  border-color: #9ca3af;
}

.theme--dark .voice-preview-btn:hover:not(:disabled) {
  background: #4b5563;
  border-color: #9ca3af;
}

.voice-preview-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Transcript customization styles */
.transcript-customization {
  margin-bottom: 1.5rem;
}

.customization-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #4a5568;
}

.theme--dark .customization-label {
  color: #a0aec0;
}

.slider-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.opacity-slider {
  flex: 1;
  height: 6px;
  -webkit-appearance: none;
  background: #e2e8f0;
  border-radius: 3px;
  outline: none;
}

.theme--dark .opacity-slider {
  background: #475569;
}

.opacity-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
}

.opacity-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #3b82f6;
  cursor: pointer;
  border: none;
}

.opacity-value {
  width: 40px;
  font-size: 0.875rem;
  text-align: right;
  color: #4a5568;
}

.theme--dark .opacity-value {
  color: #a0aec0;
}

.color-picker-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.current-color {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid #cbd5e0;
}

.theme--dark .current-color {
  border-color: #475569;
}

.color-picker {
  -webkit-appearance: none;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  background: transparent;
}

.color-picker::-webkit-color-swatch-wrapper {
  padding: 0;
}

.color-picker::-webkit-color-swatch {
  border: none;
  border-radius: 4px;
}

.reset-button {
  margin-left: auto;
  background: #f1f5f9;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.theme--dark .reset-button {
  background: #334155;
  color: #94a3b8;
}

.reset-button:hover {
  background: #e2e8f0;
  color: #475569;
}

/* Background upload styles */
.background-upload {
  width: 100%;
}

.upload-bg-label {
  display: block;
  cursor: pointer;
  width: 100%;
}

.bg-preview-container {
  width: 100%;
  height: 140px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #cbd5e0;
  background-color: #f8fafc;
  transition: all 0.3s ease;
}

.theme--dark .bg-preview-container {
  border-color: #475569;
  background-color: #1e293b;
}

.bg-preview-container:hover {
  border-color: #3b82f6;
  background-color: #f1f5f9;
}

.bg-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.bg-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: #64748b;
}

.theme--dark .bg-placeholder {
  color: #94a3b8;
}

/* Speaker customization grid */
.speakers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

.speaker-customize-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.speaker-image-upload {
  width: 100%;
}

.preview-container {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #cbd5e0;
  background-color: #f8fafc;
  margin: 0 auto;
  transition: all 0.3s ease;
}

.theme--dark .preview-container {
  border-color: #475569;
  background-color: #1e293b;
}

.preview-container:hover {
  border-color: #3b82f6;
  transform: scale(1.05);
}

.image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #64748b;
  text-align: center;
  padding: 0.5rem;
}

.theme--dark .upload-placeholder {
  color: #94a3b8;
}

.file-input {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  overflow: hidden;
}

.speaker-fields {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
}

.name-input, .role-input, .voice-select {
  width: 100%;
  padding: 0.5rem;
  font-size: 0.875rem;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  transition: all 0.3s;
  background-color: #ffffff;
  color: #1a202c;
}

.theme--dark .name-input,
.theme--dark .role-input,
.theme--dark .voice-select {
  border-color: #475569;
  background-color: #1e293b;
  color: #e2e8f0;
}

.name-input:focus,
.role-input:focus,
.voice-select:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

/* Voice selection styles */
.voice-select-container {
  position: relative;
}

.voice-select-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #4a5568;
}

.theme--dark .voice-select-label {
  color: #a0aec0;
}

.voice-select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.25em 1.25em;
  padding-right: 2rem;
}

.theme--dark .voice-select {
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%239CA3AF' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
}

/* Voice selected indicator */
.voice-selected .voice-select-label {
  color: #3b82f6;
}

.theme--dark .voice-selected .voice-select-label {
  color: #60a5fa;
}

.voice-selected .voice-select-label svg {
  color: #3b82f6;
}

.theme--dark .voice-selected .voice-select-label svg {
  color: #60a5fa;
}

/* Reset buttons */
.reset-image-button, .reset-background-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  margin: 0.5rem auto 0;
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
  color: #ef4444;
  background-color: #fee2e2;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.theme--dark .reset-image-button,
.theme--dark .reset-background-button {
  background-color: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.reset-image-button:hover,
.reset-background-button:hover {
  background-color: #fecaca;
}

.theme--dark .reset-image-button:hover,
.theme--dark .reset-background-button:hover {
  background-color: rgba(239, 68, 68, 0.3);
}

.reset-background-button {
  width: fit-content;
  margin-top: 0.75rem;
}

/* Mobile responsive design */
@media (max-width: 768px) {
  .speakers-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 1rem;
  }

  .provider-buttons {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .provider-btn {
    flex-direction: row;
    justify-content: flex-start;
    padding: 0.75rem;
    gap: 0.75rem;
  }

  .provider-settings {
    padding: 0.75rem;
  }

  .panel-title {
    font-size: 0.8125rem;
  }

  .settings-title {
    font-size: 0.875rem;
  }
}

@media (max-width: 480px) {
  .speakers-grid {
    grid-template-columns: 1fr;
  }

  .color-picker-container {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .reset-button {
    margin-left: 0;
    margin-top: 0.5rem;
  }
}

/* Enhanced animations */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.provider-settings {
  animation: slideDown 0.3s ease-out;
}

/* Accessibility improvements */
.voice-select:focus,
.setting-slider:focus,
.checkbox-label input:focus + .checkbox-custom {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .provider-btn {
    border-width: 3px;
  }

  .provider-btn.active {
    background: #1d4ed8;
    color: white;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .provider-btn,
  .preview-container,
  .provider-settings {
    transition: none;
    animation: none;
  }
}
</style>
